import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import PageCard from './components/PageCard';
import useDefaultModal from './components/hooks/useDefaultModal';
import { API_BASE_URL } from '../config/ApiConfig';
import { formatCurrencyView } from './utils/currencyUtils';

function ExecucaoOrcamentaria() {
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const anoBase = localStorage.getItem('anoBase');

  const { showDefaultModal } = useDefaultModal();

  // Estados para seleção de período
  const anoAtual = new Date().getFullYear();
  const mesAtual = new Date().getMonth() + 1;
  
  const [anoFinal, setAnoFinal] = useState(anoBase || anoAtual.toString());
  const [mesFinal, setMesFinal] = useState(() => {
    const ano = parseInt(anoBase || anoAtual);
    return ano === anoAtual ? mesAtual : 12;
  });
  const [mesInicial] = useState(1); // Sempre janeiro
  const [anoInicial, setAnoInicial] = useState(anoBase || anoAtual.toString());

  const [dados, setDados] = useState(null); // Dados do backend
  const [isLoading, setIsLoading] = useState(false);
  const [programas, setProgramas] = useState([]);
  const [programaFiltro, setProgramaFiltro] = useState('');

  // Buscar ano inicial do orçamento sempre que o programa filtro mudar
  useEffect(() => {
    const buscarAnoInicial = async () => {
      if (!cnpj || !token) return;
      try {
        const params = { cnpjAssociacao: cnpj };
        if (programaFiltro && programaFiltro !== '') {
          params.idPrograma = parseInt(programaFiltro);
        }
        const response = await axios.get(`${API_BASE_URL.ORCAMENTO}/ano-inicial`, {
          params,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data) {
          setAnoInicial(response.data.toString());
        }
      } catch (error) {
        console.error('Erro ao buscar ano inicial do orçamento:', error);
      }
    };
    buscarAnoInicial();
  }, [programaFiltro, cnpj, token]);

  // Ajustar mês final quando o ano final mudar
  useEffect(() => {
    const ano = parseInt(anoFinal);
    if (ano === anoAtual) {
      setMesFinal(mesAtual);
    } else if (ano < anoAtual) {
      setMesFinal(12);
    }
  }, [anoFinal, anoAtual, mesAtual]);

  const meses = [
    { num: 1, nome: 'Janeiro' }, { num: 2, nome: 'Fevereiro' },
    { num: 3, nome: 'Março' }, { num: 4, nome: 'Abril' },
    { num: 5, nome: 'Maio' }, { num: 6, nome: 'Junho' },
    { num: 7, nome: 'Julho' }, { num: 8, nome: 'Agosto' },
    { num: 9, nome: 'Setembro' }, { num: 10, nome: 'Outubro' },
    { num: 11, nome: 'Novembro' }, { num: 12, nome: 'Dezembro' },
  ];

  // Função para validar período
  const validarPeriodo = () => {
    const anoIni = parseInt(anoInicial);
    const anoFin = parseInt(anoFinal);

    if (anoIni > anoFin) {
      showDefaultModal('warning', 'O ano inicial não pode ser maior que o ano final');
      return false;
    }

    if (anoIni === anoFin && mesInicial > mesFinal) {
      showDefaultModal('warning', 'No mesmo ano, o mês inicial não pode ser maior que o mês final');
      return false;
    }

    return true;
  };

  // UseEffect para carregar lista de programas
  useEffect(() => {
    const carregarProgramas = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL.PROGRAMA}/all`, {
          params: { cnpjAssociacao: cnpj },
          headers: { Authorization: `Bearer ${token}` },
        });
        const lista = Array.isArray(response.data) ? response.data : [];
        setProgramas(lista);
        if (lista.length === 1) {
          setProgramaFiltro(lista[0].id.toString());
        }
      } catch (error) {
        console.error('Erro ao carregar programas:', error);
        setProgramas([]);
      }
    };
    
    if (cnpj && token) {
      carregarProgramas();
    }
  }, [cnpj, token]);

  // UseEffect para carregar dados do backend quando período ou programa mudar
  useEffect(() => {
    const carregarDados = async () => {
      if (!cnpj || !token || !validarPeriodo()) {
        return;
      }

      setIsLoading(true);
      try {
        const params = {
          cnpjAssociacao: cnpj,
          anoInicial: parseInt(anoInicial),
          mesInicial: parseInt(mesInicial),
          anoFinal: parseInt(anoFinal),
          mesFinal: parseInt(mesFinal),
        };

        // Adicionar filtro de programa se selecionado
        if (programaFiltro && programaFiltro !== '') {
          params.idPrograma = parseInt(programaFiltro);
        }

        const response = await axios.get(`${API_BASE_URL.EXECUCAO_ORCAMENTARIA}/periodo`, {
          params,
          headers: { Authorization: `Bearer ${token}` },
        });

        setDados(response.data);
      } catch (error) {
        showDefaultModal('error', 'Erro ao carregar dados de execução. Verifique sua conexão.');
        setDados(null);
      } finally {
        setIsLoading(false);
      }
    };

    carregarDados();
    // eslint-disable-next-line
  }, [mesInicial, anoInicial, mesFinal, anoFinal, programaFiltro, cnpj, token]);

  // Função para obter status de execução
  const getStatusExecucao = (percentual) => {
    if (percentual <= 50) return { texto: 'Baixa execução', cor: 'info' };
    if (percentual <= 80) return { texto: 'Execução moderada', cor: 'warning' };
    if (percentual <= 100) return { texto: 'Alta execução', cor: 'success' };
    return { texto: 'Execução excedida', cor: 'danger' };
  };

  // Função para renderizar linha da tabela
  const renderTableRow = (item, index) => {
    const orcado = item.orcado || 0;
    const redistribuido = item.redistribuido || 0;
    const pago = item.pago || 0;
    const valorAtual = orcado + redistribuido;
    const percentualExecutado = item.percentualExecutado || 0;
    const status = getStatusExecucao(percentualExecutado);
    
    let rowClass = '';
    let content = '';
    
    if (item.tipo === 'programa') {
      rowClass = 'table-success fw-bold';
      content = <span style={{ fontWeight: 'bold', fontSize: '1.05em' }}>{item.descricaoPrograma}</span>;
    } else if (item.tipo === 'acao') {
      rowClass = 'table-info';
      content = <span className="ps-3" style={{ fontWeight: 'bold' }}>{item.descricaoAcao}</span>;
    } else if (item.tipo === 'subacao') {
      rowClass = 'table-primary';
      content = (
        <span className="ps-5" style={{ fontWeight: 'bold', color: 'inherit', fontSize: '0.95em' }}>
          {item.descricaoSubAcao}
        </span>
      );
    } else if (item.tipo === 'item') {
      if (item.idSubAcao) {
        content = (
          <span className="ps-5 ms-3" style={{ fontWeight: 'normal', color: 'inherit', fontSize: '0.92em' }}>
            {item.descricaoItem}
          </span>
        );
      } else {
        content = (
          <span className="ps-5" style={{ fontWeight: 'normal', color: 'inherit', fontSize: '0.92em' }}>
            {item.descricaoItem}
          </span>
        );
      }
    }
    
    return (
      <tr key={index} className={rowClass} style={item.tipo === 'item' ? { background: '#eef6f7' } : {}}>
        <td>{content}</td>
        <td className="text-end">{formatCurrencyView(orcado)}</td>
        <td className={`text-end ${redistribuido >= 0 ? 'text-success' : 'text-danger'}`}>
          {formatCurrencyView(redistribuido)}
        </td>
        <td className="text-end fw-bold">{formatCurrencyView(valorAtual)}</td>
        <td className="text-end">{formatCurrencyView(pago)}</td>
        <td className={`text-end fw-bold ${(item.saldoDisponivel || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
          {formatCurrencyView(item.saldoDisponivel || 0)}
        </td>
        <td className="text-center">
          <span className={`badge bg-${status.cor}`}>
            {percentualExecutado.toFixed(1)}%
          </span>
        </td>
      </tr>
    );
  };

  return (
      <PageCard
        title="Execucao Orcamentaria"
        icon="bi-bar-chart-line"
        scrollable={true}
      >
        {/* Cabeçalho */}
        <div className="d-flex justify-content-between align-items-center pt-1 pb-3 border-bottom">
          
          <div className="d-flex align-items-center gap-3">
            {/* Seleção de Programa */}
            <div className="d-flex align-items-center gap-2">
              <small className="text-muted fw-bold">Programa:</small>
              <select
                className="form-select form-select-sm"
                value={programaFiltro}
                onChange={(e) => setProgramaFiltro(e.target.value)}
                style={{ width: '200px' }}
              >
                <option value="">
                  {programas.length === 0 ? 'Carregando...' : 'Todos os Programas'}
                </option>
                {programas.map((prog) => (
                  <option key={prog.id} value={prog.id}>
                    {prog.descricao || prog.nome}
                  </option>
                ))}
              </select>
              {programas.length > 0 && (
                <span className="badge bg-secondary" style={{ fontSize: '0.7rem' }}>
                  {programas.length}
                </span>
              )}
            </div>
            
            {/* Período Inicial */}
            <div className="d-flex align-items-center gap-2">
              <small className="text-muted fw-bold">De:</small>
              <select
                className="form-select form-select-sm"
                value={mesInicial}
                disabled
                style={{ width: '110px' }}
              >
                <option value={1}>Janeiro</option>
              </select>
              <input
                type="number"
                className="form-control form-control-sm"
                value={anoInicial}
                readOnly
                style={{ width: '80px', backgroundColor: '#e9ecef', cursor: 'default' }}
              />
            </div>
            
            {/* Período Final */}
            <div className="d-flex align-items-center gap-2">
              <small className="text-muted fw-bold">Até:</small>
              <select
                className="form-select form-select-sm"
                value={mesFinal}
                onChange={(e) => setMesFinal(parseInt(e.target.value))}
                style={{ width: '110px' }}
              >
                {meses.map((mes) => (
                  <option key={mes.num} value={mes.num}>
                    {mes.nome}
                  </option>
                ))}
              </select>
              <input
                type="number"
                className="form-control form-control-sm"
                value={anoFinal}
                onChange={(e) => setAnoFinal(e.target.value)}
                min="2020"
                max={new Date().getFullYear()}
                style={{ width: '80px' }}
              />
            </div>
          </div>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="mt-4 text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p className="mt-2 text-muted">Carregando dados...</p>
          </div>
        )}

        {/* Resumo de Totais */}
        {!isLoading && dados && dados.itens && dados.itens.length > 0 && (
          <div className="mt-4">
            <div className="row">
              <div className="col-md-3">
                <div className="card border-primary h-100">
                  <div className="card-body text-center">
                    <h6 className="card-title text-primary">
                      <i className="bi bi-wallet me-1"></i>
                      Orçado
                    </h6>
                    <p className="card-text h5 text-primary mb-0">
                      {formatCurrencyView(dados.totalOrcado || 0)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-warning h-100">
                  <div className="card-body text-center">
                    <h6 className="card-title text-warning">
                      <i className="bi bi-cash-coin me-1"></i>
                      Pago
                    </h6>
                    <p className="card-text h5 text-danger mb-0">
                      {formatCurrencyView(dados.totalPago || 0)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-info h-100">
                  <div className="card-body text-center">
                    <h6 className="card-title text-info">
                      <i className="bi bi-piggy-bank me-1"></i>
                      Saldo
                    </h6>
                    <p className={`card-text h5 mb-0 ${(dados.totalSaldoDisponivel || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                      {formatCurrencyView(dados.totalSaldoDisponivel || 0)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-dark h-100">
                  <div className="card-body text-center">
                    <h6 className="card-title text-dark">
                      <i className="bi bi-percent me-1"></i>
                      Executado
                    </h6>
                    <p className="card-text h5 mb-0">
                      <span className={`badge bg-${getStatusExecucao(dados.percentualExecutadoGeral || 0).cor}`}>
                        {(dados.percentualExecutadoGeral || 0).toFixed(2)}%
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mensagem quando não há dados */}
        {!isLoading && (!dados || !dados.itens || dados.itens.length === 0) && (
          <div className="mt-4 text-center">
            <div className="alert alert-info">
              <i className="bi bi-info-circle me-2"></i>
              Nenhum dado encontrado para o período selecionado.
              {console.log('⚠️ Nenhum dado para exibir. Estado de dados:', dados)}
            </div>
          </div>
        )}

        {/* Tabela de Dados Hierárquicos */}
        {!isLoading && dados && dados.itens && dados.itens.length > 0 && (
          <div className="mt-4">
            {programaFiltro && (
              <h6 className="text-secondary mb-3">
                <i className="bi bi-table me-2"></i>
                {programas.find(p => p.id === parseInt(programaFiltro))?.descricao || 'Programa Selecionado'}
              </h6>
            )}
            <div className="table-responsive">
              <table className="table table-hover table-sm">
                <thead className="table-secondary">
                  <tr>
                    <th rowSpan="2" className="align-middle">Descrição</th>
                    <th colSpan="3" className="text-center">Orçamento</th>
                    <th rowSpan="2" className="text-end align-middle">Pago</th>
                    <th rowSpan="2" className="text-end align-middle">Saldo</th>
                    <th rowSpan="2" className="text-center align-middle">Executado</th>
                  </tr>
                  <tr>
                    <th className="text-center">Inicial</th>
                    <th className="text-center">Redistribuído</th>
                    <th className="text-center">Atualizado</th>
                  </tr>
                </thead>
                <tbody>
                  {dados.itens.map((item, index) => renderTableRow(item, index))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Informações sobre Status */}
        <div className="mt-4 p-3 bg-light rounded">
          <h6 className="text-secondary mb-3">
            <i className="bi bi-info-circle me-2"></i>
            Guia de Status de Execução
          </h6>
          <div className="row">
            <div className="col-md-6">
              <h6 className="text-primary mb-2">Conceitos:</h6>
              <ul className="small">
                <li>
                  <strong>Total Orçado:</strong> Soma de todos os valores
                  previstos no orçamento para o período
                </li>
                <li>
                  <strong>Total Pago:</strong> Soma de todas as despesas que
                  foram efetivamente pagas no período
                </li>
                <li>
                  <strong>Saldo:</strong> Diferença entre o orçado e o pago
                  (Orçado - Pago)
                </li>
                <li>
                  <strong>% Executado:</strong> Percentual do orçamento que foi
                  utilizado no período
                </li>
              </ul>
            </div>
            <div className="col-md-6">
              <h6 className="text-primary mb-2">Classificações:</h6>
              <ul className="small">
                <li>
                  <span className="badge bg-info me-1">Baixa execução</span>{' '}
                  ≤ 50% do orçamento
                </li>
                <li>
                  <span className="badge bg-warning me-1">
                    Execução moderada
                  </span>{' '}
                  51% - 80% do orçamento
                </li>
                <li>
                  <span className="badge bg-success me-1">Alta execução</span>{' '}
                  81% - 100% do orçamento
                </li>
                <li>
                  <span className="badge bg-danger me-1">
                    Execução excedida
                  </span>{' '}
                  &gt; 100% do orçamento
                </li>
              </ul>
            </div>
          </div>
        </div>
      </PageCard>
  );
}

export default ExecucaoOrcamentaria;
